import React from "react";
import { motion } from "framer-motion";
import {
  Leaf,
  Globe,
  TrendingUp,
  CheckCircle,
  Shield,
  Users,
  Zap,
  BarChart3,
  Award,
  ArrowRight,
  Target,
  AlertCircle,
  Gauge,
  Lightbulb,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const ISCC_Certification_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1a6b2f] via-[#2d8a47] to-[#3ba85f] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-lime-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Globe size={18} className="text-lime-300" />
            <span>International Sustainability Leadership</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            ISCC Certification
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Demonstrate your commitment to sustainable sourcing, carbon
            footprint reduction, and environmentally responsible practices
            across your global supply chain and operations.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is ISCC Certification?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                ISCC (International Sustainability & Carbon Certification) is an
                independent verification system for companies proving commitment
                to sustainable sourcing, carbon footprint reduction, and
                environmentally responsible practices. It applies to biofuels,
                biomass, food and feed, and forestry products.
              </p>
              <p>
                The certification ensures compliance with international
                sustainability principles covering environmental, social, and
                governance (ESG) factors:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Sustainable land use and resource management",
                  "Reduction of greenhouse gas emissions",
                  "Responsible sourcing of raw materials",
                  "Traceability and transparency in supply chain",
                  "Social responsibility and fair labor practices",
                  "Compliance with global sustainability standards",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-green-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of ISCC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Leaf,
                title: "Sustainable Resource Management",
                desc: "Ensures raw materials are sourced sustainably, respecting regulations and minimizing land-use changes.",
              },
              {
                icon: TrendingUp,
                title: "Carbon Footprint Reduction",
                desc: "Measures and supports carbon reduction through practices that mitigate climate change.",
              },
              {
                icon: Globe,
                title: "Traceability and Transparency",
                desc: "Provides end-to-end traceability across supply chain from raw material to finished product.",
              },
              {
                icon: Users,
                title: "Social Responsibility",
                desc: "Promotes fair labor practices and human rights protection within the supply chain.",
              },
              {
                icon: Shield,
                title: "Comprehensive Supply Chain Audits",
                desc: "Thorough audits verify compliance with ISCC standards and adherence to best practices.",
              },
              {
                icon: Gauge,
                title: "Environmental Compliance",
                desc: "Ensures adherence to environmental protection criteria and sustainability benchmarks.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-green-500 transition-all"
                >
                  <Icon size={32} className="text-green-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why ISCC Certification Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Market Demand for Sustainable Products",
                  desc: "Meets increasing consumer demand for sustainable and ethically sourced products with clear credible assurance.",
                },
                {
                  title: "Regulatory Compliance",
                  desc: "Ensures compliance with regulations requiring sustainable sourcing and carbon reduction.",
                },
                {
                  title: "Climate Change Mitigation",
                  desc: "Supports efforts to reduce greenhouse gas emissions and achieve carbon neutrality.",
                },
                {
                  title: "Enhanced Brand Reputation",
                  desc: "Positions your business as a sustainability leader, attracting eco-conscious consumers and partners.",
                },
                {
                  title: "Supply Chain Accountability",
                  desc: "Ensures suppliers meet sustainability standards, increasing transparency and responsible partnerships.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-green-50 rounded-lg border-l-4 border-green-500"
                >
                  <Star
                    size={24}
                    className="text-green-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-green-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of ISCC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Award,
                title: "Demonstrated Sustainability Commitment",
                desc: "Independent verification enhances credibility and demonstrates commitment to responsible practices.",
              },
              {
                icon: Shield,
                title: "Regulatory Compliance",
                desc: "Ensures compliance with both local and international regulations on carbon and sourcing.",
              },
              {
                icon: Globe,
                title: "Global Market Access",
                desc: "Enables access to global markets where sustainable sourcing and carbon reduction are required.",
              },
              {
                icon: TrendingUp,
                title: "Increased Consumer Confidence",
                desc: "Enhances brand reputation through demonstrated commitment to sustainable production.",
              },
              {
                icon: BarChart3,
                title: "Competitive Advantage",
                desc: "Provides competitive edge in industries where sustainability is increasingly important.",
              },
              {
                icon: Leaf,
                title: "Environmental Leadership",
                desc: "Positions your organization as an environmental and sustainability leader.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-green-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Achieve ISCC Certification
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Join the ISCC Certification System",
                  desc: "Register with ISCC and access resources necessary for certification including guidelines and reporting tools.",
                },
                {
                  step: "2",
                  title: "Self-Assessment",
                  desc: "Conduct thorough self-assessment of sustainability practices focusing on carbon emissions and resource use.",
                },
                {
                  step: "3",
                  title: "Develop Sustainability Action Plans",
                  desc: "Create action plans based on self-assessment results to improve practices aligned with ISCC standards.",
                },
                {
                  step: "4",
                  title: "Supply Chain Evaluation",
                  desc: "Evaluate suppliers to ensure they meet ISCC sustainability criteria and integrate standards across supply chain.",
                },
                {
                  step: "5",
                  title: "Audits and Documentation",
                  desc: "Undergo external audit to verify compliance with ISCC standards and document sustainability practices.",
                },
                {
                  step: "6",
                  title: "Continuous Monitoring and Reporting",
                  desc: "Monitor sustainability progress and ensure ongoing compliance through regular audits and reporting.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-green-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-lime-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from ISCC Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Biofuel and Bioenergy Production",
              "Agricultural and Food Production",
              "Forestry and Wood Processing",
              "Biomass and Renewable Materials",
              "Supply Chain and Trading",
              "Feed and Animal Products Industry",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-green-500 text-center"
              >
                <Leaf size={32} className="text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#1a6b2f] via-[#2d8a47] to-[#3ba85f] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <Leaf size={40} className="mx-auto mb-4 text-lime-300" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for ISCC Certification?
            </h3>
            <p className="text-green-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert ISCC certification consultancy,
              helping businesses improve sustainability performance and carbon
              management.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-lime-300 flex-shrink-0"
                />
                ISCC Self-Assessment and Gap Analysis
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-lime-300 flex-shrink-0"
                />
                Sustainability Strategy Development
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-lime-300 flex-shrink-0"
                />
                Supply Chain Engagement and Compliance
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-lime-300 flex-shrink-0"
                />
                Carbon Footprint Assessment and Plans
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-lime-300 flex-shrink-0"
                />
                Ongoing Monitoring and Re-certification Support
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-lime-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-lime-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-green-700 font-bold hover:bg-lime-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default ISCC_Certification_Details;
